import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  X, Users, Pencil, Crown, HelpCircle, Heart, Settings as SettingsIcon,
  ChevronRight, TrendingUp, Zap, CheckCircle2, ShieldCheck, Shield, Clock, Download,
  Lock, Sparkles, ChartBar,
} from "lucide-react";
import { getCached } from "@/lib/cache";
import { assessment as assessmentApi, progress as progressApi } from "@/lib/api";
import { scoreBand } from "@/lib/uxCopy";
import VerifyPaymentModal from "@/pages/report/VerifyPaymentModal";

/**
 * AccountDrawer — quick dashboard opened from the top-right avatar
 * (or the sidebar profile card on desktop). Ported pixel-for-pixel
 * from the shared Kiddo+ reference. On mobile it takes the whole
 * viewport (native-app feel); on desktop (≥md) it renders as a
 * centered/right-aligned panel over a dimmed backdrop so the parent
 * always has the surrounding app in peripheral view.
 */

const WORDMARK = "/assets/images/kiddo-wordmark.png";


const MENU_ICON = {
  green:  "bg-teal-600/15 dark:bg-teal-400/15 text-teal-700 dark:text-teal-300",
  blue:   "bg-blue-100 dark:bg-blue-900/40 text-blue-500 dark:text-blue-400",
  orange: "bg-orange-100 dark:bg-orange-900/40 text-orange-500 dark:text-orange-400",
  purple: "bg-purple-100 dark:bg-purple-900/40 text-purple-500 dark:text-purple-400",
  mint:   "bg-teal-600/15 dark:bg-teal-400/15 text-teal-700 dark:text-teal-300",
  violet: "bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400",
};

// Domain colours used for the score bars — matches the reference (red / blue / green).
const BAR_PALETTE = ["#EF4444", "#3B82F6", "#22C55E", "#F97316", "#8B5CF6", "#0D9488"];

function ageInYearsMonths(dob) {
  if (!dob) return "";
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  let months = now.getMonth() - d.getMonth();
  if (now.getDate() < d.getDate()) months -= 1;
  if (months < 0) { years -= 1; months += 12; }
  years = Math.max(0, years);
  months = Math.max(0, months);
  if (years === 0) return `${months} months`;
  return `${years} year${years === 1 ? "" : "s"} ${months} month${months === 1 ? "" : "s"}`;
}

function domainLabel(key = "") {
  return key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// Activity bolt tints mirror the reference (orange · green · blue).
const ACTIVITY_TINTS = [
  { bg: "bg-[#FFE4CC]", fg: "text-[#F97316]" },
  { bg: "bg-teal-600/20",   fg: "text-teal-700" },
  { bg: "bg-[#DCEEFF]", fg: "text-[#0EA5E9]" },
];

export default function AccountDrawer({ open, onClose, user, child, onLogout }) {
  const navigate = useNavigate();
  const [report, setReport] = useState(() => getCached("report:latest") || null);
  const [compare, setCompare] = useState(() => getCached("progress:compare") || null);
  const [reportList, setReportList] = useState(() => getCached("progress:reports") || []);
  const [daily, setDaily] = useState(() => getCached("activities:daily")?.daily || []);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyDone, setVerifyDone] = useState(false);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Warm up any missing data when the drawer opens so numbers appear on
  // first paint even before the parent tab has loaded them.
  useEffect(() => {
    if (!open) return;
    if (!report) assessmentApi.latest().then((r) => setReport(r)).catch(() => {});
    if (!compare) progressApi.compare().then((c) => setCompare(c)).catch(() => {});
    if (!reportList.length) {
      assessmentApi.list()
        .then((list) => {
          if (list?.length) {
            const sorted = list.slice().sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
            setReportList(sorted);
          }
        })
        .catch(() => {});
    }
  }, [open, report, compare, reportList.length]);

  const parentFirstName = useMemo(() => {
    const n = (user?.full_name || "Parent").trim().split(/\s+/)[0];
    return n || "Parent";
  }, [user]);

  const childName = child?.name || "Your child";
  const childAge = ageInYearsMonths(child?.dob);
  const childGender = child?.gender
    ? child.gender.charAt(0).toUpperCase() + child.gender.slice(1)
    : "";
  const profileComplete = Boolean(child?.name && child?.dob && child?.gender);

  // Overall score — prefer live report, fall back to compare snapshot.
  const domains = Array.isArray(report?.domain_scores) ? report.domain_scores : [];
  const overallScore = useMemo(() => {
    if (report?.progress_overall?.percentage != null) return Math.round(report.progress_overall.percentage);
    if (domains.length === 0) return null;
    const sum = domains.reduce((acc, d) => acc + (Number(d.percentage) || 0), 0);
    return Math.round(sum / domains.length);
  }, [report, domains]);

  /* Improvement vs previous assessment (absolute point delta, matches /home + /progress) */
  const improvement = useMemo(() => {
    const avgPct = (arr) => (Array.isArray(arr) && arr.length)
      ? Math.round(arr.reduce((s, d) => s + Number(d.percentage || 0), 0) / arr.length) : 0;
    const sorted = reportList.slice().sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    const prev = sorted.length >= 2 ? sorted[sorted.length - 2] : null;
    return (prev && overallScore != null) ? (overallScore - avgPct(prev.domain_scores)) : null;
  }, [reportList, overallScore]);

  const overallDeltaPct = useMemo(() => {
    if (!compare?.has_comparison) return null;
    const cur = Number(compare?.current?.overall_percentage);
    const prev = Number(compare?.previous?.overall_percentage);
    if (!Number.isFinite(cur) || !Number.isFinite(prev) || prev === 0) return null;
    return Math.round(((cur - prev) / prev) * 100);
  }, [compare]);

  const topActivities = (daily || []).slice(0, 3);
  const topDomains = domains.slice(0, 3);

  const go = (to) => { onClose(); navigate(to); };

  const handleRequestVerification = async () => {
    if (!report?.id) return;
    setVerifyLoading(true);
    try {
      await assessmentApi.requestVerification(report.id);
      setReport((prev) => prev ? { ...prev, verification_requested: true } : prev);
      setVerifyDone(true);
      setShowVerifyModal(false);
    } catch { /* fail silently */ } finally {
      setVerifyLoading(false);
    }
  };

  const isVerified = report?.verified_by_therapist;
  const isPending = !isVerified && (report?.verification_requested || verifyDone);

  const items = [
    { key: "manage-child", icon: Users,       tone: "green",  label: "Manage Child",   to: "/child" },
    { key: "edit-profile", icon: Pencil,      tone: "blue",   label: "Edit Profile",   to: "/edit-profile" },
    { key: "subscription", icon: Crown,       tone: "orange", label: "Subscription",   to: "/subscription" },
    { key: "settings",     icon: SettingsIcon,tone: "purple", label: "Settings",       to: "/settings" },
    { key: "help",         icon: HelpCircle,  tone: "mint",   label: "Help & FAQs",    to: "/faqs" },
    { key: "about",        icon: Heart,       tone: "violet", label: "About Kiddo+",   to: "/about" },
  ];

  if (!open) return null;

  return (
    <>
    <div className="fixed inset-0 z-[60] flex md:items-start md:justify-end" data-testid="account-drawer">
      {/* Desktop-only dim backdrop (mobile is full-screen with no backdrop) */}
      <button
        type="button"
        aria-label="Close menu"
        onClick={onClose}
        className="hidden md:block absolute inset-0 bg-ink/40 backdrop-blur-sm"
      />

      {/* Panel — full-screen on mobile, right-hand sheet on desktop */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Account menu"
        className="
          relative w-full md:w-[440px]
          h-full md:h-[calc(100vh-2rem)]
          md:my-4 md:mr-4
          bg-kp-bg dark:bg-kp-bg
          md:rounded-3xl md:shadow-[0_24px_60px_-12px_rgba(42,31,61,0.35)]
          md:border md:border-kp-border/60
          flex flex-col overflow-hidden
        "
      >
        {/* Top bar — X left · Brand text centered */}
        <div className="sticky top-0 z-10 bg-kp-bg/95">
          <div className="max-w-md mx-auto flex items-center px-4 py-3">
            <button
              type="button"
              onClick={onClose}
              aria-label="Close menu"
              data-testid="account-drawer-close"
              className="w-10 h-10 rounded-full grid place-items-center -ml-1 hover:bg-kp-surfaceAlt active:scale-95 transition"
            >
              <X className="w-5 h-5 text-kp-ink" strokeWidth={2.2} />
            </button>
            <div className="flex-1 flex items-center justify-center">
              <img src={WORDMARK} alt="Kiddo+" className="h-7 w-auto" />
            </div>
            <div className="w-10 h-10" aria-hidden="true" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain">
          <div className="max-w-md mx-auto px-4 pt-4 pb-8 space-y-4">
        {/* Child card */}
        <section
          className="bg-kp-surface rounded-3xl border border-kp-border shadow-[0_2px_6px_rgba(0,0,0,0.05)] px-5 pt-6 pb-4 text-center"
          data-testid="drawer-child-card"
        >
          <button
            type="button"
            onClick={() => go("/child")}
            aria-label="View child profile"
            data-testid="drawer-child-avatar"
            className="mx-auto w-24 h-24 rounded-full overflow-hidden ring-[3px] ring-kp-primary bg-kp-surfaceAlt grid place-items-center text-kp-ink font-heading text-3xl font-bold active:scale-95 transition-transform"
          >
            {child?.photo
              ? <img src={child.photo} alt={childName} className="w-full h-full object-cover" />
              : (childName || "?").trim().charAt(0).toUpperCase()}
          </button>
          <p className="font-kp font-extrabold text-[18px] text-kp-ink mt-3 leading-tight" data-testid="drawer-child-name">
            {childName}
          </p>
          <p className="text-[13px] text-kp-ink2 mt-1">
            {[childAge, childGender].filter(Boolean).join(" · ") || "Complete profile to get started"}
          </p>
          {profileComplete && (
            <p className="text-[13px] text-teal-700 dark:text-teal-400 font-bold mt-1.5 inline-flex items-center gap-1" data-testid="drawer-profile-complete">
              <CheckCircle2 className="w-4 h-4 fill-teal-600 text-white" strokeWidth={2.4} />
              Profile Complete
            </p>
          )}
        </section>

        {/* Pro Gate card — only for free users */}
        {user?.plan !== "pro" && (
          <section
            className="relative rounded-3xl overflow-hidden border border-amber-200/80 dark:border-amber-700/40 shadow-[0_2px_12px_rgba(245,158,11,0.15)]"
            data-testid="drawer-pro-gate-card"
          >
            {/* Background gradient — two divs, one per theme (avoids JIT gradient-stop dark: issues) */}
            <div className="absolute inset-0 dark:hidden bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50" />
            <div className="absolute inset-0 hidden dark:block" style={{ background: "linear-gradient(135deg, #231508 0%, #1b0e05 60%, #1c0a03 100%)" }} />
            {/* Decorative circle */}
            <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-amber-200/30 dark:bg-amber-500/10" />
            <div className="relative px-5 py-4">
              <div className="flex items-center gap-2.5 mb-3">
                <span className="w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-900/50 grid place-items-center shrink-0">
                  <Crown className="w-5 h-5 text-amber-600 dark:text-amber-400" strokeWidth={2.2} fill="currentColor" />
                </span>
                <div>
                  <p className="font-kp font-extrabold text-[15px] text-amber-900 dark:text-amber-100 leading-tight">Upgrade to Pro</p>
                  <p className="text-[11px] text-amber-700/80 dark:text-amber-400 mt-0.5">Unlock the full Kiddo+ experience</p>
                </div>
              </div>
              <ul className="space-y-1.5 mb-4">
                {[
                  { icon: ChartBar,   text: "Full domain progress reports" },
                  { icon: Sparkles,   text: "Personalised daily activity plans" },
                  { icon: Lock,       text: "Activity library & expert insights" },
                ].map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/50 grid place-items-center shrink-0">
                      <Icon className="w-3 h-3 text-amber-600 dark:text-amber-400" strokeWidth={2.3} />
                    </span>
                    <span className="text-[12px] font-semibold text-amber-900 dark:text-amber-200">{text}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/subscription"
                onClick={onClose}
                data-testid="drawer-pro-upgrade-btn"
                className="block w-full text-center bg-amber-500 hover:bg-amber-600 dark:bg-amber-600 dark:hover:bg-amber-500 active:scale-[0.98] text-white font-extrabold text-[13px] rounded-2xl py-2.5 transition-all shadow-sm"
              >
                See Plans &amp; Pricing
              </Link>
            </div>
          </section>
        )}

        {/* Verification + Download card */}
        {report && (
          <section
            className="bg-kp-surface rounded-3xl border border-kp-border shadow-[0_2px_6px_rgba(0,0,0,0.05)] px-4 py-3 flex items-center gap-3"
            data-testid="drawer-verification-card"
          >
            {isVerified ? (
              <span className="shrink-0 w-9 h-9 rounded-full grid place-items-center bg-[#22C55E]/15">
                <ShieldCheck className="w-5 h-5 text-[#22C55E]" strokeWidth={2.3} />
              </span>
            ) : isPending ? (
              <span className="shrink-0 w-9 h-9 rounded-full grid place-items-center bg-amber-100">
                <Clock className="w-5 h-5 text-amber-600" strokeWidth={2.3} />
              </span>
            ) : (
              <span className="shrink-0 w-9 h-9 rounded-full grid place-items-center bg-teal-600/15">
                <Shield className="w-5 h-5 text-teal-700" strokeWidth={2.3} />
              </span>
            )}
            <div className="flex-1 min-w-0">
              {isVerified ? (
                <>
                  <p className="text-[13px] font-bold text-green-700 dark:text-green-400 leading-tight">
                    Verified by {report.verification?.by || "Therapist"}
                  </p>
                  <p className="text-[11px] text-kp-ink2 mt-0.5 truncate">
                    {report.verification?.credentials || report.verification?.role || "Certified Specialist"}
                  </p>
                </>
              ) : isPending ? (
                <>
                  <p className="text-[13px] font-bold text-amber-700 dark:text-amber-400 leading-tight">Verification Pending</p>
                  <p className="text-[11px] text-kp-ink2 mt-0.5">Being reviewed by a therapist</p>
                </>
              ) : (
                <>
                  <p className="text-[13px] font-bold text-kp-ink leading-tight">Therapist Verification</p>
                  <button
                    type="button"
                    onClick={() => setShowVerifyModal(true)}
                    data-testid="drawer-request-verification-btn"
                    className="text-[12px] font-bold text-teal-700 dark:text-teal-400 mt-0.5 active:opacity-70 transition-opacity"
                  >
                    Request Verification →
                  </button>
                </>
              )}
            </div>
            <Link
              to="/download-center"
              onClick={onClose}
              data-testid="drawer-download-icon"
              aria-label="Download Center"
              className="shrink-0 w-9 h-9 rounded-full grid place-items-center bg-kp-surfaceAlt hover:bg-kp-border/40 transition-colors"
            >
              <Download className="w-[18px] h-[18px] text-kp-ink2" strokeWidth={2.2} />
            </Link>
          </section>
        )}

        {/* Affirmation banner */}
        <section
          className="rounded-2xl bg-teal-600/15 dark:bg-teal-400/10 border border-teal-600/25 dark:border-teal-400/20 px-4 py-3 text-center"
          data-testid="drawer-affirmation"
        >
          <p className="text-[13px] font-bold text-teal-700 dark:text-teal-300 leading-snug">
            Great job, {parentFirstName}! Consistency today, progress tomorrow.
          </p>
        </section>

        {/* Today's Activities */}
        <section data-testid="drawer-today-activities">
          <div className="flex items-center justify-between mb-2 px-1">
            <h2 className="font-kp font-extrabold text-[17px] text-kp-ink">Today&apos;s Activities</h2>
            <Link
              to="/activities"
              onClick={onClose}
              data-testid="drawer-activities-view-all"
              className="text-[13px] font-bold text-kp-primary hover:underline"
            >
              View all
            </Link>
          </div>
          {topActivities.length === 0 ? (
            <div className="bg-kp-surface rounded-3xl border border-kp-border shadow-[0_2px_6px_rgba(0,0,0,0.05)] p-4">
              <p className="text-[13px] text-kp-ink2 text-center">No activities loaded yet.</p>
            </div>
          ) : (
            <ul className="bg-kp-surface rounded-3xl border border-kp-border shadow-[0_2px_6px_rgba(0,0,0,0.05)] divide-y divide-kp-border/60 overflow-hidden">
              {topActivities.map((a, i) => {
                const tint = ACTIVITY_TINTS[i % ACTIVITY_TINTS.length];
                const domain = a.domain || a.category || (a.domains?.[0]) || "";
                return (
                  <li key={a.id}>
                    <Link
                      to={`/activities/${a.id}`}
                      onClick={onClose}
                      data-testid={`drawer-activity-${a.id}`}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-kp-surfaceAlt active:bg-kp-surfaceAlt/70 transition"
                    >
                      <span className={`shrink-0 w-9 h-9 rounded-full grid place-items-center ${tint.bg} ${tint.fg}`}>
                        <Zap className="w-[18px] h-[18px]" strokeWidth={2.4} fill="currentColor" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-kp font-bold text-[14px] text-kp-ink leading-tight truncate">{a.title}</p>
                        <p className="text-[11px] text-kp-ink2 mt-0.5 truncate">
                          {a.duration_min || 10} min{domain ? ` · ${domainLabel(domain)}` : ""}
                        </p>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Progress Overview — color-coded card matching /report mobile view */}
        <section data-testid="drawer-progress-overview">
          <h2 className="font-kp font-extrabold text-[17px] text-kp-ink mb-2 px-1">Progress Overview</h2>
          {overallScore != null ? (() => {
            const tone = overallScore >= 75 ? "green" : overallScore >= 34 ? "yellow" : "red";
            const cardBg  = { green: "bg-teal-50 dark:bg-teal-900/20",  yellow: "bg-amber-50 dark:bg-amber-900/20",  red: "bg-red-50 dark:bg-red-900/20" }[tone];
            const scoreColor = { green: "text-teal-700 dark:text-teal-300", yellow: "text-amber-600 dark:text-amber-400", red: "text-red-600 dark:text-red-400" }[tone];
            const bandBg  = { green: "bg-teal-600/15 text-teal-800 dark:bg-teal-400/20 dark:text-teal-300", yellow: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300", red: "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400" }[tone];
            const bandLabel = { green: "On track", yellow: "Emerging", red: "Needs support" }[tone];
            const childFirst = childName.split(" ")[0];
            const msg = {
              green:  `${childFirst} is doing great! Developing well across most areas.`,
              yellow: `${childFirst} is making progress — a little extra focus in some areas will help.`,
              red:    `${childFirst} needs some support in a few areas — you're in the right place.`,
            }[tone];
            return (
              <Link to="/report" onClick={onClose} data-testid="drawer-overall-score-card" className={`block rounded-3xl border border-kp-border shadow-[0_2px_6px_rgba(0,0,0,0.05)] p-4 ${cardBg}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[12px] text-kp-ink2">Overall Development Score</p>
                  <span data-tone={tone} className={`text-[11px] font-bold rounded-full px-2.5 py-0.5 ${bandBg}`}>{bandLabel}</span>
                </div>
                <p className={`font-kp font-extrabold text-[42px] leading-none mt-1 ${scoreColor}`} data-testid="drawer-overall-score">
                  {overallScore}<span className="text-kp-ink2 text-[20px]">/100</span>
                </p>
                {improvement !== null && improvement !== 0 ? (
                  <div className="flex items-center gap-1 mt-1.5" data-testid="drawer-score-improvement">
                    <TrendingUp
                      className={`w-4 h-4 shrink-0 ${improvement > 0 ? "text-teal-600" : "text-red-500"}`}
                      strokeWidth={2.3}
                    />
                    <span className={`text-[13px] font-bold ${improvement > 0 ? "text-teal-600" : "text-red-500"}`}>
                      {improvement > 0 ? "+" : ""}{improvement}% since last
                    </span>
                  </div>
                ) : improvement === 0 ? (
                  <p className="text-[12px] text-kp-ink2/70 mt-1.5 italic">same as last assessment</p>
                ) : null}
                <p className="text-[13px] text-kp-ink2 mt-2 leading-snug">{msg}</p>
              </Link>
            );
          })() : (
            <div className="bg-kp-surface rounded-3xl border border-kp-border shadow-[0_2px_6px_rgba(0,0,0,0.05)] p-4 text-center">
              <p className="text-[13px] text-kp-ink2">No assessment yet</p>
              <Link to="/activities" onClick={onClose} className="text-[13px] font-bold text-kp-primary mt-1 block">Start your first assessment →</Link>
            </div>
          )}
        </section>

        {/* Domains */}
        {topDomains.length > 0 && (
          <section data-testid="drawer-domains">
            <div className="flex items-center justify-between mb-2 px-1">
              <h2 className="font-kp font-extrabold text-[17px] text-kp-ink">Domains</h2>
              <Link
                to="/report"
                onClick={onClose}
                data-testid="drawer-domains-view-all"
                className="text-[12px] font-bold text-kp-primary hover:underline"
              >
                View All →
              </Link>
            </div>
            <div className="bg-kp-surface rounded-3xl border border-kp-border shadow-[0_2px_6px_rgba(0,0,0,0.05)] p-4 space-y-3">
              {topDomains.map((d, i) => {
                const pct = Math.max(0, Math.min(100, Number(d.percentage) || 0));
                const raw = Number(d.score) != null && d.max_score
                  ? `${Math.round(pct)}/100`
                  : `${Math.round(pct)}/100`;
                const barColor = d.color || BAR_PALETTE[i % BAR_PALETTE.length];
                return (
                  <div key={d.domain_key || i}>
                    <div className="flex items-center justify-between text-[13px] mb-1">
                      <span className="font-kp font-bold text-kp-ink truncate pr-2">{d.domain_name || domainLabel(d.domain_key)}</span>
                      <span className="text-kp-ink2 font-semibold shrink-0">{raw}</span>
                    </div>
                    <div className="h-1.5 bg-kp-surfaceAlt rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-[width]" style={{ width: `${pct}%`, background: barColor }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Menu */}
        <nav className="space-y-2.5" aria-label="Account menu">
          {items.map((it) => <DrawerMenuRow key={it.key} item={it} onClose={onClose} />)}
        </nav>

        {/* Log out (kept subtle at the bottom) */}
        <div className="pt-2">
          <button
            type="button"
            onClick={() => { onClose(); onLogout && onLogout(); }}
            data-testid="account-drawer-logout"
            className="w-full text-center text-[13px] font-bold text-coral hover:underline py-3"
          >
            Log Out
          </button>
        </div>
          </div>
        </div>
      </div>
    </div>

    {/* Therapist verification modal */}
    <VerifyPaymentModal
      open={showVerifyModal}
      onOpenChange={setShowVerifyModal}
      onConfirm={handleRequestVerification}
      loading={verifyLoading}
    />
    </>
  );
}

function DrawerMenuRow({ item, onClose }) {
  const Icon = item.icon;
  const style = MENU_ICON[item.tone] || MENU_ICON.green;
  return (
    <Link
      to={item.to}
      onClick={onClose}
      data-testid={`account-drawer-item-${item.key}`}
      className="flex items-center gap-3 rounded-3xl bg-kp-surface border border-kp-border shadow-[0_2px_6px_rgba(0,0,0,0.05)] px-4 py-3.5 hover:bg-kp-surfaceAlt active:scale-[0.995] transition"
    >
      <span className={`shrink-0 w-10 h-10 rounded-full grid place-items-center ${style}`}>
        <Icon className="w-5 h-5" strokeWidth={2.3} />
      </span>
      <span className="flex-1 font-kp font-bold text-[15px] text-kp-ink leading-tight">{item.label}</span>
      <ChevronRight className="w-5 h-5 text-kp-ink2 shrink-0" strokeWidth={2.2} />
    </Link>
  );
}
